import { useEffect } from "react";
import {
  getRealtimeTransport,
  rtEnabled,
  useNotificationStore,
  useUserStore,
  type SlumbrNotification,
} from "@slumbr/core";
import { useBackgroundServiceManager } from "@slumbr/app-sdk";
import type { TopicItem } from "@gomomento/sdk-web";

import {
  broadcastFromLeader,
  ensureLeaderElection,
  isTabLeader,
  subscribeToBroadcasts,
  subscribeToLeaderChange,
} from "./tab-leader";

import { generateAccessToken } from "@/apps/messenger/huddle01/config";
import { CallPersistentWidget } from "@/apps/messenger/components/call-persistent-widget";
import type { Conversation, Message } from "@/apps/messenger/types";
import { useMessengerStore } from "@/apps/messenger/stores/messenger-store";
import { deleteCachedConversation, setCachedConversations } from "@/apps/messenger/storage/idb-cache";
import { useNotesStore } from "@/apps/notes/stores/notes-store";
import { useAppBadgesStore } from "@/stores/app-badges-store";
import { playNotificationSound } from "@/utils/notification-sounds";
import { notificationToWebOptions, showWebNotification } from "@/utils/web-notifications";

type RealtimeEvent = {
  type?: string;
  appId?: string;
  conversationId?: string;
  conversation?: Conversation;
  message?: Message;
  senderId?: string;
  roomId?: string;
  callType?: "voice" | "video";
  callerId?: string;
  callerName?: string;
  messages?: Message[];
  lastMessage?: Message;
  notification?: SlumbrNotification;
  isTyping?: boolean;
  displayName?: string;
  id?: string;
  title?: string;
  body?: string;
  read?: boolean;
  /** `user.profile.updated` payload: a peer changed their avatar/displayName. */
  userId?: string;
  avatarUrl?: string;
};

export function useRealtimeSubscriptions() {
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);
  const userId = useUserStore((s) => s.user?.id);

  useEffect(() => {
    if (!isAuthenticated || !userId) return;

    let disposed = false;
    let sub: { unsubscribe: () => void } | null = null;
    const typingTimers = new Map<string, number>();

    const shouldPlaySound = (appId: string) => {
      // Leader tab only — follower tabs replay the same event through this
      // handler for UI coherence, and each used to play its own copy of the
      // sound (3 tabs = 3 simultaneous pings).
      if (!isTabLeader()) return false;
      const prefs = useUserStore.getState().user?.preferences.notifications;
      if (!prefs || prefs.sound === false) return false;
      return !prefs.mutedApps.includes(appId);
    };

    const handleRealtimeItem = (_topic: string, item: TopicItem) => {
      if (disposed || useUserStore.getState().user?.id !== userId) return;
      let event: RealtimeEvent;
      try {
        const raw = item.valueString();
        event = JSON.parse(raw) as RealtimeEvent;
        if (import.meta.env.DEV) {
          console.debug("[Realtime] event:", event.type);
        }
      } catch {
        return;
      }

      // ── Progression (XP / level-up / achievement toasts) ───────────

      if (typeof event.type === "string" && event.type.startsWith("progression.")) {
        window.dispatchEvent(new CustomEvent("slumbr:progression", { detail: event }));
        return;
      }

      // ── Villagr (party invites etc.) — forwarded to the game when it's open ──

      if (typeof event.type === "string" && event.type.startsWith("villagr.")) {
        window.dispatchEvent(new CustomEvent("villagr:rt", { detail: event }));
        return;
      }

      // ── Notifications ──────────────────────────────────────────────

      if (event.type === "notification.created" && event.notification) {
        const alreadySeen = useNotificationStore
          .getState()
          .notifications.some((n) => n.id === event.notification!.id);
        useNotificationStore.getState().addNotification(event.notification);
        if (
          !alreadySeen &&
          event.notification.appId !== "messenger" &&
          shouldPlaySound(event.notification.appId)
        ) {
          playNotificationSound("general");
        }
        if (!alreadySeen) {
          void showWebNotification(notificationToWebOptions(event.notification));
        }
        return;
      }

      if (event.id && event.title && typeof event.read === "boolean") {
        const alreadySeen = useNotificationStore
          .getState()
          .notifications.some((n) => n.id === event.id);
        const notification = event as SlumbrNotification;
        useNotificationStore.getState().addNotification(notification);
        const appId = event.appId ?? "general";
        if (!alreadySeen && appId !== "messenger" && shouldPlaySound(appId)) {
          playNotificationSound("general");
        }
        if (!alreadySeen) {
          void showWebNotification(notificationToWebOptions(notification));
        }
        return;
      }

      // ── New message — insert directly from event (no API call) ─────

      if (event.type === "chat.message.created" && event.message) {
        const message = event.message as Message;
        const conversationId = message.conversationId;
        const store = useMessengerStore.getState();
        const activeConvId = store.activeConversationId;
        const existing = store.messages[conversationId]?.some(
          (m) => m.id === message.id
        );

        store.updateMessagesInPlace(conversationId, [message]);
        // Dual transport delivery and reconnect replay must be side-effect free.
        if (existing) return;

        // Encrypted messages arrive with a placeholder `content` ({type:
        // "encrypted"}) and the real payload sealed in `encryptedEnvelope`.
        // Kick off decryption immediately — the store action replaces the
        // placeholder content in place once the ratchet step succeeds.
        if (
          message.isEncrypted &&
          message.encryptedEnvelope &&
          message.senderId !== userId
        ) {
          void store
            .decryptMessageContent(conversationId, message.id)
            .catch((err) => {
              console.warn("[Realtime] decrypt on receive failed:", err);
            });
        }

        // If the conversation isn't in local state, the user previously
        // soft-deleted it. The new message means the conversation should
        // reappear from the user's POV; refetch from the server so the
        // re-hydrated row carries fresh participant + last-message data.
        const hasConversation = store.conversations.some(
          (c) => c.id === conversationId
        );
        if (!hasConversation) {
          void store.fetchConversations().catch(() => {});
        }

        const isCurrent = conversationId === activeConvId && document.visibilityState === "visible" && document.hasFocus();
        const muted = store.conversations.find((c) => c.id === conversationId)?.isMuted;
        const isOwn = event.senderId === userId || message.senderId === userId;
        if (!isOwn) {
          if (isCurrent) {
            store.markAsRead(conversationId);
            useAppBadgesStore.getState().clearBadge("messenger");
          } else {
            useMessengerStore.setState((state) => ({
              conversations: state.conversations.map((conversation) =>
                conversation.id === conversationId
                  ? {
                      ...conversation,
                      unreadCount: conversation.unreadCount + 1,
                      lastMessage: message,
                      updatedAt: message.createdAt,
                    }
                  : conversation
              ),
            }));
            useAppBadgesStore.getState().incrementBadge("messenger");
          }
          if (!muted && !isCurrent && shouldPlaySound("messenger")) playNotificationSound("chat");

          // OS notification when the tab isn't focused (showWebNotification
          // self-suppresses when the page is visible+focused). Pulls sender
          // + preview from the realtime message so we don't need a separate
          // notification.created event for non-mention DMs.
          if (!isCurrent && !muted) {
            const conv = useMessengerStore
              .getState()
              .conversations.find((c) => c.id === conversationId);
            const sender = conv?.participants.find(
              (participant) => participant.userId === message.senderId
            );
            const content = message.content as { type?: string; text?: string };
            const preview =
              content?.type === "text"
                ? (content.text ?? "").slice(0, 160)
                : content?.type === "image"
                  ? "📷 Photo"
                  : content?.type === "gif"
                    ? "GIF"
                    : content?.type === "voice"
                      ? "🎙️ Voice message"
                      : "New message";
            const isGroup = conv?.type === "group";
            const title = isGroup
              ? `${sender?.displayName ?? "Someone"} in ${conv?.name ?? "Group chat"}`
              : (sender?.displayName ?? "New message");
            void showWebNotification({
              title,
              body: preview,
              tag: `chat-${conversationId}`,
              renotify: true,
              ...(sender?.avatarUrl ? { icon: sender.avatarUrl } : {}),
              link: `/messenger/${conversationId}?msg=${message.id}`,
              data: {
                smartGroup: "chat",
                conversationId,
                messageId: message.id,
                senderId: message.senderId,
                senderName: sender?.displayName,
                latestPreview: preview,
                isGroup,
              },
            });
          }
        }
        return;
      }

      if (event.type === "chat.typing" && event.conversationId && event.senderId) {
        if (event.senderId === userId) return;
        const store = useMessengerStore.getState();
        const key = `${event.conversationId}:${event.senderId}`;
        const existingTimer = typingTimers.get(key);
        if (existingTimer) window.clearTimeout(existingTimer);

        if (event.isTyping) {
          store.setTyping(
            event.conversationId,
            event.senderId,
            event.displayName ?? "Someone"
          );
          typingTimers.set(
            key,
            window.setTimeout(() => {
              useMessengerStore
                .getState()
                .clearTyping(event.conversationId!, event.senderId!);
              typingTimers.delete(key);
            }, 5000)
          );
        } else {
          store.clearTyping(event.conversationId, event.senderId);
          typingTimers.delete(key);
        }
        return;
      }

      // ── Message updated (reactions, pin, edit, delete) ────────────

      if (event.type === "chat.message.updated" && event.message) {
        const message = event.message as Message;
        const conversationId = message.conversationId;
        // updateMessagesInPlace merges by id — this replaces the existing copy
        useMessengerStore.getState().updateMessagesInPlace(conversationId, [message]);
        return;
      }

      // ── New conversation — insert directly from event ─────────────

      if (event.type === "chat.conversation.created" && event.conversation) {
        const store = useMessengerStore.getState();
        const exists = store.conversations.some((c) => c.id === event.conversation!.id);
        store.syncConversationsFromRemote([event.conversation as Conversation]);
        // Badge for incoming new conversation not from self
        if (!exists && event.senderId !== userId) {
          useMessengerStore.setState((state) => ({
            conversations: state.conversations.map((conversation) =>
              conversation.id === event.conversationId
                ? {
                    ...conversation,
                    unreadCount: Math.max(conversation.unreadCount, 1),
                  }
                : conversation
            ),
          }));
          useAppBadgesStore.getState().incrementBadge("messenger");
          if (shouldPlaySound("messenger")) playNotificationSound("chat");
        }
        return;
      }

      // ── Conversation updated (theme, name, participants, etc.) ─────

      if (event.type === "chat.conversation.updated" && event.conversation) {
        useMessengerStore
          .getState()
          .syncConversationsFromRemote([event.conversation as Conversation]);
        return;
      }

      // ── Conversation deleted (group nuked / DM fully hard-deleted) ──

      if (event.type === "chat.conversation.deleted" && event.conversationId) {
        const deletedId = event.conversationId;
        useMessengerStore.setState((state) => {
          const messages = { ...state.messages };
          delete messages[deletedId];
          return {
            conversations: state.conversations.filter((c) => c.id !== deletedId),
            messages,
            activeConversationId:
              state.activeConversationId === deletedId
                ? null
                : state.activeConversationId,
          };
        });
        void deleteCachedConversation(deletedId);
        void setCachedConversations(useMessengerStore.getState().conversations);
        return;
      }

      // ── Own per-user state changed on ANOTHER device (read/pin/mute) ──

      if (event.type === "chat.userstate.updated" && event.conversationId) {
        const convId = event.conversationId;
        const flags = event as {
          lastReadAt?: string;
          isPinned?: boolean;
          isMuted?: boolean;
          isArchived?: boolean;
        };
        useMessengerStore.setState((state) => ({
          conversations: state.conversations.map((conversation) =>
            conversation.id === convId
              ? {
                  ...conversation,
                  ...(flags.lastReadAt ? { unreadCount: 0 } : {}),
                  ...(flags.isPinned !== undefined ? { isPinned: flags.isPinned } : {}),
                  ...(flags.isMuted !== undefined ? { isMuted: flags.isMuted } : {}),
                  ...(flags.isArchived !== undefined
                    ? { isArchived: flags.isArchived }
                    : {}),
                }
              : conversation
          ),
        }));
        void setCachedConversations(useMessengerStore.getState().conversations);
        return;
      }

      if (event.type === "chat.state.updated") {
        return;
      }

      // ── Note share events — refresh the user's "Shared with me" list ──

      if (
        event.type === "note.share.created" ||
        event.type === "note.share.revoked"
      ) {
        void useNotesStore.getState().fetchSharedNotes();
        return;
      }

      // ── Peer profile updated — patch cached participants in place ──
      //
      // A user this account has a conversation with changed their avatar or
      // display name. The server has already fanned this event out to every
      // peer; here we patch the messenger store's conversations[].participants[]
      // entries so the new avatar shows up without waiting for the next
      // list-conversations fetch. Same payload also busts the IDB cache
      // implicitly (it persists conversations on every change).
      if (event.type === "user.profile.updated" && event.userId) {
        const peerUserId = event.userId;
        const nextAvatarUrl = event.avatarUrl;
        const nextDisplayName = event.displayName;
        useMessengerStore.setState((state) => ({
          conversations: state.conversations.map((conversation) => {
            const hit = conversation.participants?.some(
              (participant) => participant.userId === peerUserId
            );
            if (!hit) return conversation;
            return {
              ...conversation,
              participants: conversation.participants.map((participant) =>
                participant.userId === peerUserId
                  ? {
                      ...participant,
                      ...(nextAvatarUrl !== undefined
                        ? { avatarUrl: nextAvatarUrl || undefined }
                        : {}),
                      ...(nextDisplayName !== undefined
                        ? { displayName: nextDisplayName || participant.displayName }
                        : {}),
                    }
                  : participant
              ),
            };
          }),
        }));
        void setCachedConversations(useMessengerStore.getState().conversations);
        return;
      }

      // ── Incoming call — show accept/deny UI, don't auto-accept ────

      if (
        event.type === "call.started" &&
        event.roomId &&
        event.callType &&
        event.callerId !== userId
      ) {
        // Don't interrupt if already in a call
        if (useMessengerStore.getState().activeCall) return;

        // Store the incoming call invitation — UI will show accept/deny prompt
        useMessengerStore.setState({
          incomingCall: {
            roomId: event.roomId!,
            conversationId: event.conversationId ?? "",
            type: event.callType!,
            callerId: event.callerId ?? "",
            callerName: event.callerName ?? "Unknown",
          },
        });
        useAppBadgesStore.getState().incrementBadge("messenger");
      }
    };

    // Leader-only Momento subscription. Sibling tabs receive events via
    // BroadcastChannel so they stay UI-coherent without paying for a
    // duplicate Momento subscription. If the user opens 3 tabs, this drops
    // realtime topic-delivery cost by 3x.
    let broadcastUnsub: (() => void) | null = null;
    let leaderUnsub: (() => void) | null = null;

    // In-app live notifications via the inbox Durable Object (flag:
    // VITE_RT_NOTIFICATIONS). Leader-scoped like the Momento sub. Events are
    // fed through the same handler; the notification dedup guards any overlap
    // with the Momento user-topic during the dual-write migration window.
    let doInbox: { close: () => void } | null = null;
    // Heal any gap: events published while a transport was down are lost, so
    // refetch the conversation list + active conversation. The store
    // debounces this to at most one network sync per 5s.
    const resyncAfterGap = () => {
      if (disposed) return;
      void useMessengerStore.getState().resync().catch(() => {});
    };
    const startInboxDo = () => {
      if (doInbox || disposed || !rtEnabled("notifications") || !userId) return;
      const channel = getRealtimeTransport().join(`inbox/${userId}`);
      const off = channel.on((raw, meta) => {
        // Inbox events are server-authored; the token is self-only, so a peer
        // frame could only come from our own session — drop it anyway.
        if (meta.fromPeer) return;
        handleRealtimeItem("inbox", {
          valueString: () => JSON.stringify(raw),
        } as TopicItem);
        try {
          broadcastFromLeader({
            type: "realtime",
            payload: { topic: "inbox", value: JSON.stringify(raw) },
          });
        } catch {
          /* follower mirror best-effort */
        }
      });
      const offReconnect = channel.onReconnect?.(resyncAfterGap);
      doInbox = {
        close: () => {
          off();
          offReconnect?.();
          channel.close();
        },
      };
    };
    const stopInboxDo = () => {
      doInbox?.close();
      doInbox = null;
    };

    let subscriptionStarting = false;
    const startSubscription = () => {
      startInboxDo();
      if (sub || disposed || subscriptionStarting) return;
      subscriptionStarting = true;
      void import("@/apps/messenger/momento-subscriber")
        .then(({ subscribeToRealtime }) => {
          if (disposed || !isTabLeader() || sub) return;
          sub = subscribeToRealtime(
            (topic, item) => {
              handleRealtimeItem(topic, item);
              // Re-broadcast to follower tabs.
              try {
                broadcastFromLeader({
                  type: "realtime",
                  payload: { topic, value: item.valueString() },
                });
              } catch (err) {
                console.warn("[Realtime] broadcast failed", err);
              }
            },
            undefined,
            // Subscription recovered after an error — heal the gap.
            resyncAfterGap
          );
        })
        .catch((err) => {
          console.error("[Realtime] failed to load subscriber:", err);
        }).finally(() => { subscriptionStarting = false; });
    };

    const stopSubscription = () => {
      sub?.unsubscribe();
      sub = null;
      stopInboxDo();
    };

    void ensureLeaderElection().then(() => {
      if (disposed) return;
      leaderUnsub = subscribeToLeaderChange((leader) => {
        if (leader) {
          startSubscription();
        } else {
          stopSubscription();
        }
      });
      // Mirror leader-broadcasted events into the follower's local handler.
      broadcastUnsub = subscribeToBroadcasts((envelope) => {
        if (isTabLeader()) return; // leader already handled it directly
        if (envelope.type !== "realtime") return;
        const payload = envelope.payload as { topic: string; value: string };
        if (!payload?.topic || typeof payload.value !== "string") return;
        // Synthesize a TopicItem-shaped object — the handler only uses
        // valueString(), so a minimal shim is fine.
        handleRealtimeItem(payload.topic, { valueString: () => payload.value } as TopicItem);
      });
    });

    // Heal gaps whenever the tab comes back to life: browsers throttle or
    // freeze hidden tabs (killing sockets without firing onclose), and a
    // laptop waking from sleep reconnects silently. The store's resync is
    // debounced to one network sync per 5s, so these listeners are cheap.
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        resyncAfterGap();
        const active = useMessengerStore.getState().activeConversationId;
        if (active && document.hasFocus()) useMessengerStore.getState().markAsRead(active);
      }
    };
    const onOnline = () => resyncAfterGap();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);

    return () => {
      disposed = true;
      for (const typingTimer of typingTimers.values()) {
        window.clearTimeout(typingTimer);
      }
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
      stopSubscription();
      broadcastUnsub?.();
      leaderUnsub?.();
    };
  }, [isAuthenticated, userId]);
}
